/**
 * useDashboardRingStateLive Hook
 *
 * Live Firestore subscription for ring item state.
 * Uses onSnapshot listeners for instant UI updates (no React Query invalidation needed).
 *
 * Firestore paths:
 * - Team-level: /teams/{teamId}/ringItemState/{itemKey}
 * - User-level: /users/{uid}/ringItemState/{itemKey}
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveTeamId } from '@/contexts/TeamContext';
import {
  type RingItemState,
  type RingItemStateClient,
  type RingItemStateMap,
  type RingItemType,
  type RingStateScope,
  getItemKey,
  getTargetScope,
  getTomorrowMorning,
  toClientState,
  shouldCleanupState,
} from '@/types/ringItemState';

// DEV-only logger to satisfy eslint no-console without changing logic
const devLog = (...args: unknown[]) => {
  if (!import.meta.env.DEV) return;
  (globalThis as Record<string, unknown>)?.console?.log?.(...args);
};
const devError = (...args: unknown[]) => {
  if (!import.meta.env.DEV) return;
  (globalThis as Record<string, unknown>)?.console?.error?.(...args);
};

// ============================================================================
// Types
// ============================================================================

export interface RingStateLiveActions {
  /** Snooze an item until tomorrow 09:00 */
  snoozeItem: (
    itemKey: string,
    itemType: RingItemType,
    entityId: string,
    scope: RingStateScope
  ) => Promise<void>;
  /** Mute an item indefinitely ("Niet meer tonen") */
  muteItem: (
    itemKey: string,
    itemType: RingItemType,
    entityId: string,
    scope: RingStateScope
  ) => Promise<void>;
  /** Unhide an item (delete ring state doc) */
  unhideItem: (itemKey: string, scope: RingStateScope) => Promise<void>;
  /** Reset all hidden items for a specific scope */
  resetAllItems: (scope: RingStateScope) => Promise<void>;
}

export interface UseDashboardRingStateLiveResult {
  /** Team-level ring state map */
  teamStateMap: RingItemStateMap;
  /** User-level ring state map */
  userStateMap: RingItemStateMap;
  /** Combined state map (team + user) */
  combinedStateMap: RingItemStateMap;
  /** Actions for modifying ring state */
  actions: RingStateLiveActions;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useDashboardRingStateLive(): UseDashboardRingStateLiveResult {
  const { user } = useAuth();
  const teamId = useActiveTeamId();
  const uid = user?.uid;

  const [teamStateMap, setTeamStateMap] = useState<RingItemStateMap>(new Map());
  const [userStateMap, setUserStateMap] = useState<RingItemStateMap>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Track loading state for both subscriptions
  const [teamLoaded, setTeamLoaded] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);

  // ========================================================================
  // Firestore Subscriptions (onSnapshot for live updates)
  // ========================================================================

  useEffect(() => {
    if (!teamId) {
      setTeamStateMap(new Map());
      setTeamLoaded(true);
      return;
    }

    // Team-level ring state: /teams/{teamId}/ringItemState
    const teamCollectionRef = collection(db, 'teams', teamId, 'ringItemState');

    const unsubscribe = onSnapshot(
      teamCollectionRef,
      (snapshot) => {
        const newMap = new Map<string, RingItemStateClient>();
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as RingItemState;
          const itemKey = docSnap.id;
          newMap.set(itemKey, toClientState(data, itemKey));
        });
        setTeamStateMap(newMap);
        setTeamLoaded(true);
        setError(null);
      },
      (err) => {
        devError('[RingStateLive] Team subscription error:', err);
        setError(err);
        setTeamLoaded(true);
      }
    );

    return () => unsubscribe();
  }, [teamId]);

  useEffect(() => {
    if (!uid) {
      setUserStateMap(new Map());
      setUserLoaded(true);
      return;
    }

    // User-level ring state: /users/{uid}/ringItemState
    const userCollectionRef = collection(db, 'users', uid, 'ringItemState');

    const unsubscribe = onSnapshot(
      userCollectionRef,
      (snapshot) => {
        const newMap = new Map<string, RingItemStateClient>();
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as RingItemState;
          const itemKey = docSnap.id;
          newMap.set(itemKey, toClientState(data, itemKey));
        });
        setUserStateMap(newMap);
        setUserLoaded(true);
        setError(null);
      },
      (err) => {
        devError('[RingStateLive] User subscription error:', err);
        setError(err);
        setUserLoaded(true);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  // Update loading state when both subscriptions are ready
  useEffect(() => {
    setIsLoading(!teamLoaded || !userLoaded);
  }, [teamLoaded, userLoaded]);

  // ========================================================================
  // Combined State Map
  // ========================================================================

  const combinedStateMap = useMemo(() => {
    const combined = new Map<string, RingItemStateClient>();
    // Add team state first
    teamStateMap.forEach((state, key) => combined.set(key, state));
    // Add user state (overwrites team state if same key exists)
    userStateMap.forEach((state, key) => combined.set(key, state));
    return combined;
  }, [teamStateMap, userStateMap]);

  // ========================================================================
  // Actions
  // ========================================================================

  const getDocRef = useCallback(
    (itemKey: string, scope: RingStateScope) => {
      if (scope === 'team' && teamId) {
        return doc(db, 'teams', teamId, 'ringItemState', itemKey);
      }
      if (scope === 'user' && uid) {
        return doc(db, 'users', uid, 'ringItemState', itemKey);
      }
      return null;
    },
    [teamId, uid]
  );

  const snoozeItem = useCallback(
    async (itemKey: string, itemType: RingItemType, entityId: string, scope: RingStateScope) => {
      const docRef = getDocRef(itemKey, scope);
      if (!docRef || !uid) {
        devError('[RingStateLive] Cannot snooze: missing docRef or uid');
        return;
      }

      const tomorrow = getTomorrowMorning();
      const data: RingItemState = {
        itemKey,
        itemType,
        entityId,
        scope,
        snoozedUntil: Timestamp.fromDate(tomorrow),
        muted: false,
        updatedAt: Timestamp.now(),
        updatedBy: uid,
      };

      await setDoc(docRef, data);
    },
    [getDocRef, uid]
  );

  const muteItem = useCallback(
    async (itemKey: string, itemType: RingItemType, entityId: string, scope: RingStateScope) => {
      const docRef = getDocRef(itemKey, scope);
      if (!docRef || !uid) {
        devError('[RingStateLive] Cannot mute: missing docRef or uid');
        return;
      }

      const data: RingItemState = {
        itemKey,
        itemType,
        entityId,
        scope,
        snoozedUntil: null,
        muted: true,
        updatedAt: Timestamp.now(),
        updatedBy: uid,
      };

      await setDoc(docRef, data);
    },
    [getDocRef, uid]
  );

  const unhideItem = useCallback(
    async (itemKey: string, scope: RingStateScope) => {
      const docRef = getDocRef(itemKey, scope);
      if (!docRef) {
        devError('[RingStateLive] Cannot unhide: missing docRef');
        return;
      }

      // Delete the doc entirely (preferred approach per spec)
      await deleteDoc(docRef);
    },
    [getDocRef]
  );

  const resetAllItems = useCallback(
    async (scope: RingStateScope) => {
      const stateMap = scope === 'team' ? teamStateMap : userStateMap;
      if (stateMap.size === 0) return;

      const batch = writeBatch(db);
      let deleteCount = 0;

      stateMap.forEach((_, itemKey) => {
        const docRef = getDocRef(itemKey, scope);
        if (docRef) {
          batch.delete(docRef);
          deleteCount++;
        }
      });

      if (deleteCount > 0) {
        await batch.commit();
      }
    },
    [teamStateMap, userStateMap, getDocRef]
  );

  // ========================================================================
  // Return
  // ========================================================================

  const actions: RingStateLiveActions = useMemo(
    () => ({
      snoozeItem,
      muteItem,
      unhideItem,
      resetAllItems,
    }),
    [snoozeItem, muteItem, unhideItem, resetAllItems]
  );

  return {
    teamStateMap,
    userStateMap,
    combinedStateMap,
    actions,
    isLoading,
    error,
  };
}

// ============================================================================
// Cleanup Helper (Strategy 1)
// ============================================================================

/**
 * Run opportunistic cleanup of expired/irrelevant ring state docs
 * Call this periodically (e.g., on dashboard load)
 */
export async function cleanupRingState(
  teamId: string | undefined,
  uid: string | undefined,
  teamStateMap: RingItemStateMap,
  userStateMap: RingItemStateMap,
  candidateKeys: Set<string>
): Promise<void> {
  const now = new Date();
  const batch = writeBatch(db);
  let deleteCount = 0;
  const maxDeletes = 25; // Limit deletes per cleanup run

  // Helper to check if we should delete
  const shouldDelete = (state: RingItemStateClient, itemKey: string): boolean => {
    // Delete if item is no longer a candidate (resolved/completed)
    if (!candidateKeys.has(itemKey)) return true;
    // Delete if expired snooze and not muted
    return shouldCleanupState(state, now);
  };

  // Cleanup team state
  if (teamId) {
    teamStateMap.forEach((state, itemKey) => {
      if (deleteCount >= maxDeletes) return;
      if (shouldDelete(state, itemKey)) {
        const docRef = doc(db, 'teams', teamId, 'ringItemState', itemKey);
        batch.delete(docRef);
        deleteCount++;
      }
    });
  }

  // Cleanup user state
  if (uid) {
    userStateMap.forEach((state, itemKey) => {
      if (deleteCount >= maxDeletes) return;
      if (shouldDelete(state, itemKey)) {
        const docRef = doc(db, 'users', uid, 'ringItemState', itemKey);
        batch.delete(docRef);
        deleteCount++;
      }
    });
  }

  if (deleteCount > 0) {
    await batch.commit();
    devLog(`[RingStateLive] Cleaned up ${deleteCount} expired/irrelevant ring state docs`);
  }
}
